import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, Fragment } from "react";
import { Box } from "../Box/Box.mjs";
import { Field } from "../private/Field/Field.mjs";
import { ClearField } from "../private/Field/ClearField.mjs";
import { getCharacterLimitStatus } from "../private/Field/getCharacterLimitStatus.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconFilter } from "../icons/IconFilter/IconFilter.mjs";
import { MenuRenderer } from "../MenuRenderer/MenuRenderer.mjs";
import { MenuItem } from "../MenuItem/MenuItem.mjs";
import { Text } from "../Text/Text.mjs";
import { Inline } from "../Inline/Inline.mjs";
import { Tag } from "../Tag/Tag.mjs";
import { verticalDivider, filterNotification } from "./SearchField.css.mjs";
import { Stack } from "../Stack/Stack.mjs";
import { FieldButtonIcon } from "../private/FieldButtonIcon/FieldButtonIcon.mjs";
const SearchField = forwardRef(
  ({
    value,
    filters,
    selectedFilters = [],
    onChange,
    onKeyDown,
    onBlur,
    onFocus,
    onClear,
    onFilter,
    onAction,
    placeholder,
    characterLimit,
    maxLength,
    id,
    clearLabel,
    actionIcon,
    actionLabel,
    inputMode,
    step,
    ...restProps
  }, forwardedRef) => {
    const defaultRef = useRef(null);
    const inputRef = forwardedRef || defaultRef;
    const clearable = Boolean(
      typeof onClear !== "undefined" && !restProps.disabled && typeof value === "string" && value.length > 0
    );
    const handleFilter = (filter) => {
      onFilter == null ? void 0 : onFilter(filter);
    };
    return /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(
        Field,
        {
          ...restProps,
          id,
          value,
          labelId: void 0,
          placeholder,
          secondaryMessage: characterLimit ? getCharacterLimitStatus({
            value,
            characterLimit
          }) : null,
          children: (overlays, fieldProps, icon, _, prefix) => /* @__PURE__ */ jsxs(Fragment, { children: [
            icon,
            prefix,
            /* @__PURE__ */ jsx(
              Box,
              {
                component: "input",
                maxLength,
                type: "text",
                value,
                onKeyDown,
                onChange,
                onFocus,
                onBlur,
                ...fieldProps,
                inputMode: inputMode || "text",
                placeholder,
                step,
                ref: inputRef
              }
            ),
            overlays,
            /* @__PURE__ */ jsxs(Box, { display: "flex", alignItems: "center", paddingRight: "xxsmall", children: [
              onClear && value ? /* @__PURE__ */ jsx(
                ClearField,
                {
                  id: `${id}-clear`,
                  hide: !clearable,
                  onClear,
                  label: clearLabel,
                  inputRef
                }
              ) : null,
              !restProps.disabled && onClear && value && onFilter ? /* @__PURE__ */ jsx(Box, { paddingX: "xxsmall", style: { height: 15 }, children: /* @__PURE__ */ jsx(Box, { className: verticalDivider }) }) : null,
              !restProps.disabled && onFilter ? /* @__PURE__ */ jsx(
                Box,
                {
                  component: "span",
                  width: "touchable",
                  height: "touchable",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  right: 0,
                  children: /* @__PURE__ */ jsx(
                    MenuRenderer,
                    {
                      offsetSpace: "small",
                      trigger: (triggerProps) => /* @__PURE__ */ jsxs(Box, { userSelect: "none", cursor: "pointer", ...triggerProps, children: [
                        (selectedFilters == null ? void 0 : selectedFilters.length) > 0 && /* @__PURE__ */ jsx(
                          Box,
                          {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "absolute",
                            background: "critical",
                            borderRadius: "circle",
                            padding: "xxxsmall",
                            zIndex: "notification",
                            className: filterNotification,
                            children: /* @__PURE__ */ jsx(Text, { size: "xsmall", weight: "strong", children: selectedFilters == null ? void 0 : selectedFilters.length })
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          FieldButtonIcon,
                          {
                            id: `filter`,
                            onMouseDown: onAction,
                            icon: /* @__PURE__ */ jsx(IconFilter, {}),
                            label: "Filter"
                          }
                        )
                      ] }),
                      children: filters == null ? void 0 : filters.map((f) => /* @__PURE__ */ jsx(
                        MenuItem,
                        {
                          weight: selectedFilters.some((selected) => selected.value === f.value) ? "strong" : "regular",
                          onClick: () => {
                            handleFilter(f);
                          },
                          children: f.label
                        },
                        f.value
                      ))
                    }
                  )
                }
              ) : null
            ] })
          ] })
        }
      ),
      filters && /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: selectedFilters == null ? void 0 : selectedFilters.map((filter) => /* @__PURE__ */ jsx(Tag, { onClear: () => handleFilter(filter), clearLabel: "Remove", children: filter.label }, filter.value)) })
    ] });
  }
);
SearchField.displayName = "SearchField";
export {
  SearchField
};
